import { getApiUrl } from "@/utils/api";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { RotateCcw, RotateCw, Loader2, RefreshCw } from "lucide-react";
import { resolveImageUrl } from "@/utils/imageUrl";

const API = getApiUrl();

/**
 * Reusable client-side image editor.
 * Props:
 *   open, onClose
 *   sourceUrl   — existing image URL (relative or absolute) to edit
 *   onSaved(newUrl) — called with the new uploaded URL after save
 *   skipWatermark — passes through to the upload endpoint (logos)
 */
export default function ImageEditor({ open, onClose, sourceUrl, onSaved, skipWatermark = true }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });

  // Edit state
  const [rotation, setRotation] = useState(0);     // degrees, multiples of 90
  const [scale, setScale] = useState(100);          // percent (10–200)
  const [brightness, setBrightness] = useState(100); // percent (0–200)
  const [contrast, setContrast] = useState(100);    // percent (0–200)

  const reset = () => {
    setRotation(0);
    setScale(100);
    setBrightness(100);
    setContrast(100);
  };

  // Load source image when modal opens
  useEffect(() => {
    if (!open || !sourceUrl) return;
    setLoading(true);
    reset();
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      setLoading(false);
    };
    img.onerror = () => {
      toast.error("Couldn't load image for editing");
      setLoading(false);
      onClose?.();
    };
    img.src = resolveImageUrl(sourceUrl);
  }, [open, sourceUrl, onClose]);

  // Re-render preview whenever any control changes
  useEffect(() => {
    if (loading || !imgRef.current || !canvasRef.current) return;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const factor = scale / 100;
    const swappedDims = rotation % 180 !== 0;
    const targetW = Math.max(1, Math.round((swappedDims ? img.naturalHeight : img.naturalWidth) * factor));
    const targetH = Math.max(1, Math.round((swappedDims ? img.naturalWidth : img.naturalHeight) * factor));
    canvas.width = targetW;
    canvas.height = targetH;
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
    ctx.save();
    ctx.translate(targetW / 2, targetH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    const drawW = img.naturalWidth * factor;
    const drawH = img.naturalHeight * factor;
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }, [loading, rotation, scale, brightness, contrast]);

  const handleSave = async () => {
    if (!canvasRef.current) return;
    setSaving(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        canvasRef.current.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Canvas export failed"))),
          "image/jpeg",
          0.92
        );
      });
      const fd = new FormData();
      fd.append("file", new File([blob], "edited.jpg", { type: "image/jpeg" }));
      const res = await axios.post(
        `${API}/admin/upload?skip_watermark=${skipWatermark}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      const newUrl = res.data?.url;
      if (!newUrl) throw new Error("No URL returned");
      onSaved?.(newUrl);
      toast.success("Image saved");
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save edited image");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="image-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Edit Image</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Preview */}
          <div className="bg-muted/40 border border-border p-3 flex items-center justify-center min-h-[260px] max-h-[420px] overflow-auto" data-testid="image-editor-preview-wrap">
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            ) : (
              <canvas
                ref={canvasRef}
                className="max-w-full max-h-[400px] object-contain shadow-sm"
                data-testid="image-editor-canvas"
              />
            )}
          </div>

          {!loading && (
            <p className="text-xs text-muted-foreground text-center" data-testid="image-editor-size">
              Output: {canvasRef.current?.width || 0} × {canvasRef.current?.height || 0} px
              {" — Original: "}{naturalSize.w} × {naturalSize.h} px
            </p>
          )}

          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Rotation */}
            <div className="space-y-2">
              <Label className="text-xs">Rotation ({rotation}°)</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
                  className="rounded-none"
                  data-testid="image-editor-rotate-left"
                >
                  <RotateCcw className="h-4 w-4 mr-1" /> Rotate left
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="rounded-none"
                  data-testid="image-editor-rotate-right"
                >
                  <RotateCw className="h-4 w-4 mr-1" /> Rotate right
                </Button>
              </div>
            </div>

            {/* Resize */}
            <div className="space-y-2">
              <Label className="text-xs">Size ({scale}%)</Label>
              <Slider
                value={[scale]}
                min={10}
                max={200}
                step={5}
                onValueChange={(v) => setScale(v[0])}
                data-testid="image-editor-scale-slider"
              />
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setScale((s) => Math.max(10, s - 10))} className="rounded-none text-xs" data-testid="image-editor-shrink">
                  Shrink −10%
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setScale((s) => Math.min(200, s + 10))} className="rounded-none text-xs" data-testid="image-editor-enlarge">
                  Enlarge +10%
                </Button>
              </div>
            </div>

            {/* Brightness */}
            <div className="space-y-2">
              <Label className="text-xs">Brightness ({brightness}%)</Label>
              <Slider
                value={[brightness]}
                min={0}
                max={200}
                step={5}
                onValueChange={(v) => setBrightness(v[0])}
                data-testid="image-editor-brightness-slider"
              />
            </div>

            {/* Contrast */}
            <div className="space-y-2">
              <Label className="text-xs">Contrast ({contrast}%)</Label>
              <Slider
                value={[contrast]}
                min={0}
                max={200}
                step={5}
                onValueChange={(v) => setContrast(v[0])}
                data-testid="image-editor-contrast-slider"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={reset}
            disabled={saving || loading}
            className="rounded-none"
            data-testid="image-editor-reset"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Reset
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onClose?.()}
            disabled={saving}
            className="rounded-none"
            data-testid="image-editor-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-none"
            data-testid="image-editor-save"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
